<template>
  <div class="wrapper">
    <div class="header-container">
      <div class="title">
        <a-icon
          type="arrow-left"
          class="breadcrumb-icon"
          @click="goBack"
        />
        <span
          v-if="isNarrowScreen"
          style="margin-left:8px;line-height:28px"
        >{{ entity && entity.name }}</span>
        <h3
          v-else
          class="title"
        >
          {{ entity && entity.name }}
        </h3>
      </div>
      <div
        v-if="isNarrowScreen && (canAbort || canRestart)"
        class="operation"
      >
        <a-dropdown>
          <a
            class="ant-dropdown-link"
            @click="e => e.preventDefault()"
          >
            <a-icon type="more" />
          </a>
          <a-menu
            slot="overlay"
            @click="handleMenuClick"
          >
            <a-menu-item
              v-if="canAbort"
              key="abort"
            >
              {{ $t("k_199ae69d") }}
            </a-menu-item>
            <a-menu-item
              v-if="canRetry"
              key="retry"
            >
              {{ $t("k_3909871e") }}
            </a-menu-item>
            <a-menu-item
              v-if="canRestart"
              key="restart"
            >
              {{ $t("k_3f7e4afa") }}
            </a-menu-item>
          </a-menu>
        </a-dropdown>
      </div>
      <div
        v-if="!isNarrowScreen"
        class="operation"
      >
        <a-alert
          v-if="isProcessing && pluginDisableDefaultAction"
          show-icon
        >
          <span slot="message">
            <span>{{ $t("k_16f3957b", { arg_0: pluginDisplayName }) }}</span>
          </span>
        </a-alert>
        <a-button
          v-if="canAbort"
          @click="abort"
        >
          {{ $t("k_199ae69d") }}
        </a-button>
        <a-button
          v-if="canRetry"
          style="margin-left:8px"
          @click="retry"
        >
          {{ $t("k_3909871e") }}
        </a-button>
        <a-button
          v-if="canRestart"
          style="margin-left:8px"
          @click="restart"
        >
          {{ $t("k_3f7e4afa") }}
        </a-button>
      </div>
    </div>
    <w-sys-spin
      v-if="initializing"
      class="center"
    />
    <render-page
      v-else-if="pageId"
      style="height:100vh;background-color:#f8f8f8"
      :project-id="projectId"
      :page-id="pageId"
    />
    <detail
      v-else-if="entity"
      style="min-height:calc(100vh - 112px)"
      :project-id="projectId"
      :env="env"
      :entity="entity"
    />
    <div
      v-else
      class="p-4 flex-grow-1 bg-white bg-clip-content"
    >
      <a-alert
        class="m-4"
        type="warning"
        :message="$t('k_60c78e5a')"
      />
    </div>
  </div>
</template>

<script>
import { $t } from '@/i18n';
import { Modal, message } from 'ant-design-vue';
import { uniq } from 'lodash';
import { getAppParams } from '@utils/path';
import { workflowApi, workflowEntityApi } from '@/services';
import RenderPage from './RenderPage.vue';
import Detail from './Detail.vue';
import { getUserId } from '@/utils/userInfo';
import { isMobileOrNarrowScreen } from '@utils/screen';
import { getWorkflowNotificationPluginList } from '@pages/modules/workflow/config/utils';

export default {
  components: {
    RenderPage,
    Detail,
  },
  inject: {
    ctx: 'ctx',
  },
  data() {
    return {
      pageId: '',
      initializing: true,
      entity: null,
      flowConf: null,
      rtx: getUserId(),
      records: [],
      retryRecord: null,
      comment: '',
    };
  },
  computed: {
    urlParams() {
      return getAppParams() || {};
    },
    projectId() {
      return this.urlParams.projectId;
    },
    env() {
      return this.urlParams.env || '';
    },
    id() {
      const { query } = this.$route;
      return query.entityid;
    },
    // 插件可以禁止默认的审批行为，全权交由第三方进行审批处理
    pluginDisableDefaultAction() {
      const pluginId = this.flowConf?.notifyPluginId;
      if (!pluginId) return false;
      const plugin = getWorkflowNotificationPluginList().find(x => x.id === pluginId);
      return !!plugin?.disableDefaultAction;
    },
    pluginDisplayName() {
      const pluginId = this.flowConf?.notifyPluginId;
      if (!pluginId) return '';
      const plugin = getWorkflowNotificationPluginList().find(x => x.id === pluginId);
      return plugin?.name ?? '';
    },
    isProcessing() {
      return this.entity?.flowStatus === 'processing';
    },
    canAbort() {
      if (this.flowConf?.disabledAbort || this.pluginDisableDefaultAction) return false;
      if (['success', 'fail', 'abort'].includes(this.entity?.flowStatus)) return false;
      return this.entity?.creator === this.rtx || this.ctx.user.isAdmin;
    },
    canRestart() {
      if (this.flowConf?.disabledRestart || this.pluginDisableDefaultAction) return false;
      return (this.entity?.creator === this.rtx || this.ctx.user.isAdmin);
    },
    canRetry() {
      const rec = this.retryRecord;
      if (!rec) return false;
      return (rec.creator === this.rtx || this.ctx.user.isAdmin);
    },
    isNarrowScreen() {
      return isMobileOrNarrowScreen();
    },
  },
  created() {
    this.init();
  },
  methods: {
    async init() {
      const entity = await this.getWorkflowEntity();
      this.initializing = false;
      if (!entity) return;
      this.records = await this.getEntityRecords();
      this.entity = entity;
      this.flowConf = JSON.parse(entity.flowConf);
      this.retryRecord = this.getFailedRecord(this.flowConf, this.records);
      this.injectInCtx(entity, this.flowConf);
      const ids = this.getIds(entity);
      if (!ids) return;
      const { pageId } = ids;
      this.pageId = pageId;
    },
    injectInCtx(entity) {
      const payload = entity.payload ? JSON.parse(entity.payload) : null;
      this.ctx.workflowEntity = entity;
      this.ctx.workflowApprovalData = payload;
    },
    async getWorkflowEntity() {
      try {
        const result = await workflowEntityApi.fetchWorkflowEntity(this.projectId, this.env, this.id);
        return result;
      } catch (err) {
        message.error($t('k_3b336c30'));
      }
    },
    async getEntityRecords() {
      try {
        const result = await workflowEntityApi.fetchEntityRecords(this.projectId, this.env, this.id);
        return result;
      } catch (err) {
        message.error($t('k_4e1905c5'));
      }
    },
    getIds(entity) {
      const { status, flowConf } = entity;
      const conf = JSON.parse(flowConf);
      const node = conf.nodes.find(node => node.id === status);
      if (!node.pageId) return null;
      const [projectId, pageId] = node.pageId.split(':');
      return { projectId, pageId };
    },
    handleMenuClick({ key }) {
      if (key === 'abort') return this.abort();
      if (key === 'restart') return this.restart();
      if (key === 'retry') return this.retry();
    },
    abort() {
      Modal.confirm({
        title: $t('k_fb78182c'),
        content: h => h('div', null, [
          h('a-input', {
            style: { marginTop: '8px' },
            props: { placeholder: $t('k_20b502a3') },
            on: { change: e => this.comment = e.target.value },
          }, null),
        ]),
        onOk: async () => {
          try {
            await workflowEntityApi.abort(this.projectId, this.env, this.id, { comment: this.comment });
            this.$router.go(0);
          } catch (error) {
            message.error($t('k_ff8f5383', { arg_0: error.message }));
          }
        },
      });
    },
    getFailedRecord(flow, records) {
      const hasAbortedResp = (record) => {
        const check = (resp) => {
          const arr = resp ? JSON.parse(resp) : [];
          return arr.some(x => x.aborted);
        };
        return check(record.entryHookResponse) || check(record.afterHookResponse);
      };
      const isAbnormalNode = (nodeId) => {
        const node = flow.nodes.find(x => x.id === nodeId);
        return node?.abnormal;
      };
      // 失败节点列表
      const failedIdList = uniq(records.filter(x => x.operation === 'pass' && isAbnormalNode(x.to))
        .map(x => x.from)
        .filter(Boolean));
      // 已经重试成功的失败节点列表
      const retryNodeIdList = records.filter(x => x.operation === 'pass'
        && failedIdList.includes(x.from)
        && !isAbnormalNode(x.to)
        && !hasAbortedResp(x)).map(x => x.from);

      // 没有重试或重试仍然失败的节点
      const failedRecList = records.filter(x => x.operation === 'pass'
        && failedIdList.includes(x.from)
        && !isAbnormalNode(x.to)
        && hasAbortedResp(x)
        && !retryNodeIdList.includes(x.from));

      // 只能有一个
      return failedRecList.length ? failedRecList[0] : null;
    },
    retry() {
      Modal.confirm({
        title: $t('k_868de658'),
        content: h => h('div', null, [
          h('p', null, $t('k_bfbda1f9')),
          h('p', null, $t('k_8aaa3d2f', { arg_0: this.flowConf.nodes.find(x => x.id === this.retryRecord.from).name, arg_1: this.retryRecord.from })),
        ]),
        onOk: async () => {
          try {
            await workflowEntityApi.retry(this.projectId, this.env, this.id, this.retryRecord.from);
            this.$router.go(0);
          } catch (error) {
            const resp = await error.response?.json();
            message.error($t('k_cb7d8494', { arg_0: resp?.error }));
          }
        },
      });
    },
    async doRestart() {
      try {
        await workflowEntityApi.restart(this.projectId, this.env, this.id);
        this.$router.go(0);
      } catch (error) {
        message.error($t('k_a0dbf204', { arg_0: error.message }));
      }
    },
    async restart() {
      const { _id: id, flowId, flowConf, objectId, payload = '' } = this.entity;
      let flow = JSON.parse(flowConf);
      try {
        // 尝试使用新的流程配置来重新发起
        flow = await workflowApi.fetchWorkflow(this.projectId, this.env, flowId);
      } catch (error) {
        console.warn($t('k_fdfa100d'));
      }
      // 使用`发起流程页面`来重新发起流程
      if (flow?.pageId) {
        const pageId = flow.pageId.replace(`${this.projectId}:`, '');
        this.$router.push({ name: pageId, query: { id: objectId, entityid: id, payload } });
        return;
      }

      Modal.confirm({
        title: $t('k_df7c42b'),
        content: $t('k_79b0dd42'),
        onOk: () => this.doRestart(),
      });
    },
    goBack() {
      this.$router.go(-1);
    },
  },
};
</script>

<style lang="scss" scoped>
.wrapper {
  position: relative;
  height: calc(100vh - var(--xy-header-height, 56px));
  display: flex;
  flex-direction: column;
  flex: 1;

  .center {
    position: absolute;
    top: 50%;
    left: 50%;
  }
}

.header-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  background: #FFFFFF;
  box-shadow: inset 0 -1px 0 0 #EEEEEE;
  border-left: 1px solid #F0F2F5;
  padding-right: 24px;

  .title {
    display: flex;
    align-items: center;
    height: 56px;
    padding-left: 24px;
    padding-right: 24px;

    h3 {
      font-size: 20px;
      color: rgba(0,0,0,0.90);
      line-height: 28px;
      margin: 0;
      padding-left: 12px;
    }
  }
}

</style>
